/* ============================================================
   September Reset Bundle Landing Page — behaviour
   Vanilla, no dependencies. Loaded with `defer`.
   Re-initialises on Shopify theme-editor section events.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var smooth = reduceMotion ? 'auto' : 'smooth';

  /* ---------- buybox gallery ---------- */

  function initGallery(root) {
    var main = root.querySelector('.srb-gallery__main');
    var rail = root.querySelector('[data-srb-gallery-rail]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-srb-gallery-thumb]'));
    if (!main || !rail || !thumbs.length) return;

    function select(btn) {
      var full = btn.getAttribute('data-full');
      if (full) {
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = full;
      }
      var alt = btn.getAttribute('data-alt');
      if (alt) main.alt = alt;
      thumbs.forEach(function (t) {
        t.setAttribute('aria-current', t === btn ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () { select(btn); });
    });

    /* arrows scroll the thumbnail rail one page at a time */
    var prev = root.querySelector('[data-srb-gallery-prev]');
    var next = root.querySelector('[data-srb-gallery-next]');

    function page(dir) {
      rail.scrollBy({ left: dir * Math.round(rail.clientWidth * 0.8), behavior: smooth });
    }
    function syncArrows() {
      var max = rail.scrollWidth - rail.clientWidth - 1;
      if (prev) prev.disabled = rail.scrollLeft <= 0;
      if (next) next.disabled = rail.scrollLeft >= max;
    }

    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });
    rail.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows, { passive: true });
    syncArrows();
  }

  /* ---------- scroll rail + progress bar ---------- */

  function initRail(root) {
    var rail = root.querySelector('[data-srb-rail]');
    var bar = root.querySelector('[data-srb-railbar]');
    if (!rail || !bar) return;

    var thumb = bar.querySelector('.srb-railbar__thumb');
    if (!thumb) return;

    function update() {
      var overflow = rail.scrollWidth - rail.clientWidth;
      if (overflow <= 1) {
        bar.hidden = true;
        return;
      }
      bar.hidden = false;
      var ratio = rail.clientWidth / rail.scrollWidth;
      var progress = rail.scrollLeft / overflow;
      thumb.style.width = (ratio * 100).toFixed(3) + '%';
      thumb.style.transform = 'translateX(' + (progress * (100 / ratio - 100)).toFixed(3) + '%)';
    }

    rail.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(rail);
    update();
  }

  /* ---------- video cards: swap poster for the real player on demand ---------- */

  function initVideos(root) {
    root.querySelectorAll('.srb-video').forEach(function (card) {
      var btn = card.querySelector('[data-srb-video-play]');
      if (!btn || btn.dataset.srbBound) return;
      btn.dataset.srbBound = '1';

      btn.addEventListener('click', function () {
        /* a Shopify-hosted video is already in the DOM */
        var video = card.querySelector('video');
        if (video) {
          card.classList.add('is-playing');
          btn.hidden = true;
          video.controls = true;
          var p = video.play();
          if (p && p.catch) p.catch(function () { video.controls = true; });
          return;
        }

        /* otherwise build the YouTube/Vimeo iframe on first press */
        var src = card.getAttribute('data-srb-video');
        if (!src) return;
        var frame = document.createElement('iframe');
        frame.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
        frame.title = btn.getAttribute('aria-label') || 'Video';
        frame.loading = 'lazy';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        frame.allowFullscreen = true;
        frame.className = 'srb-video__frame';
        card.classList.add('is-playing');
        card.appendChild(frame);
        btn.hidden = true;
      });
    });
  }

  /* ---------- sticky header: hide on scroll down, show on scroll up ---------- */

  var headerApi = null;

  /* Publishes the real header height as --srb-header-h so in-page anchors
     can offset by it. Runs even when auto-hide is off. */
  function measureHeader() {
    var header = document.querySelector('.srb-section--header');
    var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--srb-header-h', h + 'px');
  }

  function initHeader() {
    measureHeader();
    window.addEventListener('resize', measureHeader, { passive: true });

    var inner = document.querySelector('[data-srb-header-autohide]');
    if (!inner) return;
    var header = inner.closest('.srb-section--header');
    if (!header || header.dataset.srbHeaderBound) return;
    header.dataset.srbHeaderBound = '1';

    var THRESHOLD = 6;      /* ignore sub-pixel jitter and rubber-banding */
    var SETTLE = 180;       /* ms of stillness that counts as "scroll finished" */
    var MAX_JUMP = 3000;    /* hard stop so a jump can never get stuck */
    var last = window.pageYOffset || 0;
    var ticking = false;
    var jumping = false;
    var settleTimer = null;
    var maxTimer = null;

    function show() { endJump(); header.classList.remove('srb-header--hidden'); }

    function endJump() {
      if (!jumping) return;
      jumping = false;
      window.clearTimeout(settleTimer);
      window.clearTimeout(maxTimer);
      last = window.pageYOffset || 0;
      measureHeader();
    }

    /* restart the settle countdown on every scroll tick of the jump —
       the freeze lasts as long as the scroll does, however far it travels
       and in whichever direction */
    function bump() {
      if (!jumping) return;
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(endJump, SETTLE);
    }

    function update() {
      ticking = false;
      var y = window.pageYOffset || 0;
      if (y < 0) y = 0;

      /* an anchor jump is in flight: stay tucked away, whichever way we
         are travelling. Scrolling UP to the target would otherwise
         re-show the header the moment the jump began. */
      if (jumping) { last = y; bump(); return; }

      /* never hide while the header is still over its own resting place */
      var offset = header.offsetHeight || 64;
      if (y <= offset) { show(); last = y; return; }

      var diff = y - last;
      if (Math.abs(diff) < THRESHOLD) return;
      if (diff > 0) header.classList.add('srb-header--hidden');
      else show();
      last = y;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });

    /* keyboard users must never land on an off-screen control */
    header.addEventListener('focusin', show);
    update();

    headerApi = {
      show: show,
      /* tuck the header away and hold it there until the jump settles */
      beginJump: function () {
        jumping = true;
        header.classList.add('srb-header--hidden');
        window.clearTimeout(maxTimer);
        maxTimer = window.setTimeout(endJump, MAX_JUMP);
        bump();
      }
    };
  }

  /* ---------- in-page CTA links: hide the header, land on the section ---------- */

  function initAnchors() {
    if (document.documentElement.dataset.srbAnchorsBound) return;
    document.documentElement.dataset.srbAnchorsBound = '1';

    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href*="#"]');
      if (!link) return;

      var href = link.getAttribute('href') || '';
      var hash = href.slice(href.indexOf('#'));
      if (hash.length < 2) return;

      /* same-document links only */
      if (link.pathname && link.pathname !== window.location.pathname) return;

      var target;
      try { target = document.querySelector(hash); } catch (err) { return; }
      if (!target) return;

      /* The header slides away for the jump, so the section shouldn't be
         pushed down by its height — drop the offset to zero and land flush
         on the section's top edge. The measured value is restored by
         endJump() once scrolling settles. */
      if (headerApi) {
        document.documentElement.style.setProperty('--srb-header-h', '0px');
        headerApi.beginJump();
      } else {
        measureHeader();
      }
    }, true);
  }

  /* ---------- boot ---------- */

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-srb-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-srb-rail-root]').forEach(initRail);
    initVideos(root);
    initHeader();
    initAnchors();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
